import { useTranslation } from "react-i18next";
import { formatEuros } from "@sundial/core";
import { useUrssaf } from "../api/queries.js";

function todayParis(): string {
  return new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

/**
 * The Home micro-entreprise card (SundialCeilingCard.dc.html): ONLY the
 * always-annual, always-global figures — the CA-encaissé ceiling bar tagged
 * "Année {year}" and VAT status. It never follows the period toggle or the
 * client filter, because the plafond and franchise are person-level by law. The
 * monthly "à mettre de côté" now lives in the Revenue card.
 */
export function CeilingCard({ filtered = false }: { filtered?: boolean }) {
  const { t } = useTranslation();
  const { data: urssaf } = useUrssaf();

  const year = todayParis().slice(0, 4);
  const ceilingPct = urssaf?.ceilingPct ?? 0;
  const barPct = Math.min(ceilingPct, 100);

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.05)",
        padding: "16px 17px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 13 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
        <div style={{ font: "700 14px Sora", color: "var(--text)" }}>{t("ceiling.title")}</div>
        {filtered && (
          <span style={{ marginLeft: "auto", font: "600 9px Sora", letterSpacing: ".05em", textTransform: "uppercase", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--muted2)", padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>
            {t("ceiling.globalChip")}
          </span>
        )}
      </div>

      {/* Annual ceiling + VAT — always global, never follows period or client. */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 13, padding: "13px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
          <div style={{ font: "600 10px Sora", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)" }}>
            {t("ceiling.caCumul", { year })}
          </div>
          <span style={{ font: "700 9px Sora", letterSpacing: ".06em", textTransform: "uppercase", background: "color-mix(in srgb, var(--primary) 14%, transparent)", color: "var(--primary-deep)", padding: "3px 7px", borderRadius: 6, whiteSpace: "nowrap" }}>
            {t("ceiling.yearChip", { year })}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: 15.5, color: "var(--text)" }}>
            {formatEuros(urssaf?.receivedYtdCents ?? 0)}
            <span style={{ color: "var(--muted2)", fontWeight: 500 }}> / {formatEuros(urssaf?.ceilingCents ?? 0)}</span>
          </span>
          <span style={{ font: "600 13px Sora", color: "var(--primary-deep)" }}>{ceilingPct.toFixed(1)} %</span>
        </div>
        <div role="progressbar" aria-valuenow={Math.round(ceilingPct)} aria-valuemin={0} aria-valuemax={100} aria-label={t("ceiling.barAria", { pct: ceilingPct.toFixed(1) })} style={{ height: 9, borderRadius: 99, background: "var(--surface)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${barPct}%`, background: "linear-gradient(90deg,var(--primary),var(--primary-bright))", borderRadius: 99 }} />
        </div>
        <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 6 }}>
          {t("ceiling.ofCeiling", { pct: ceilingPct.toFixed(1), ceiling: formatEuros(urssaf?.ceilingCents ?? 0) })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 11, paddingTop: 11, borderTop: "1px solid var(--border)" }}>
          <span style={{ width: 24, height: 24, flex: "none", borderRadius: 7, background: urssaf?.vatApplicable ? "color-mix(in srgb, var(--warning) 18%, transparent)" : "var(--surface)", color: "var(--warning)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l9 16H3z" />
              <path d="M12 10v4M12 17h.01" />
            </svg>
          </span>
          <span style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 600 }}>
            {urssaf?.vatApplicable ? t("urssaf.vatApplicable") : t("urssaf.vatFranchise")}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 7, marginTop: 12 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted2)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", marginTop: 1 }}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8h.01M11 12h1v4h1" />
        </svg>
        <span style={{ fontSize: 11.5, color: "var(--muted2)", lineHeight: 1.45 }}>{t("ceiling.personLevelNote")}</span>
      </div>
    </div>
  );
}
